// Billing — per-action subscription (PLAN-C4.4). The tenant pays per active compliance
// action (activiteit); the Stripe subscription quantity = its active-action count,
// computed server-side. Card data never touches Sympllex (Stripe-hosted embed).
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { PER_ACTION_PRICE, formatPerActionUnit, formatMonthlyEstimate } from "@sympllex/billing/plans";
import { ApiError } from "@sympllex/api-client";
import { api } from "../lib/api";
import { STRIPE_PUBLISHABLE_KEY } from "../lib/env";
import { useSession } from "../lib/session";
import { PageHeader } from "../components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

export function BillingRoute() {
  const session = useSession();
  const [checkout, setCheckout] = useState<{ clientSecret: string; actionCount: number | null } | null>(null);

  const start = useMutation({
    mutationFn: () =>
      api.createCheckoutSession({
        tenantId: session.activeTenantId,
        returnUrl: window.location.origin + "/billing?status=complete",
      }),
    onSuccess: (r) => setCheckout({ clientSecret: r.clientSecret, actionCount: r.actionCount ?? null }),
  });

  if (checkout && stripePromise) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Complete your subscription" />
        {checkout.actionCount != null && (
          <p className="text-sm text-foreground">
            {checkout.actionCount} active compliance action{checkout.actionCount === 1 ? "" : "s"} ×{" "}
            {formatPerActionUnit()} = <strong>{formatMonthlyEstimate(checkout.actionCount)}</strong>
          </p>
        )}
        <div>
          <Button variant="outline" size="sm" onClick={() => { setCheckout(null); start.reset(); }}>← Back</Button>
        </div>
        <div>
          <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret: checkout.clientSecret }}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Plans & billing"
        description="Aquino Compliance bills per compliance action — you pay only for the actions the law actually requires of you."
      />
      {!STRIPE_PUBLISHABLE_KEY && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Stripe publishable key not configured (set <code>VITE_STRIPE_PUBLISHABLE_KEY</code>). Checkout opens once it is set.
        </p>
      )}
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle className="text-lg">{PER_ACTION_PRICE.productName}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <div className="text-2xl font-semibold">{formatPerActionUnit()}</div>
          <p className="text-sm text-muted-foreground">
            Your bill = the number of active compliance actions in your programme × the per-action price. It scales as your
            obligations grow or shrink.
          </p>
          <Button className="mt-2 w-full" onClick={() => start.mutate()} disabled={start.isPending || !stripePromise}>
            {start.isPending ? "Starting…" : "Start subscription"}
          </Button>
          {start.isError && (
            <p className="mt-2 text-sm text-destructive">
              {start.error instanceof ApiError ? `Server ${start.error.status}: ${JSON.stringify(start.error.body)}` : (start.error as Error).message}
            </p>
          )}
        </CardContent>
      </Card>

      <ManageBilling />
    </div>
  );
}

// Billing self-serve (D8): the Stripe-hosted Customer Portal (change card / cancel / tax id) + an
// invoice/receipt history. The portal session + invoices are resolved server-side for the signed-in
// tenant; a 409 means "no subscription yet" (the portal only exists once you've checked out).
interface Invoice { id: string; number: string | null; status: string | null; amountDueCents: number; amountPaidCents: number; currency: string; created: number; hostedInvoiceUrl: string | null; invoicePdf: string | null }

function ManageBilling() {
  const invoices = useQuery({
    queryKey: ["invoices"],
    queryFn: () => api.listInvoices() as unknown as Promise<{ invoices: Invoice[] }>,
  });
  const portal = useMutation({
    mutationFn: async () => {
      const r = await api.createPortalSession(window.location.origin + "/billing");
      if (r.url) window.location.href = r.url;
      return r;
    },
  });
  const rows = invoices.data?.invoices ?? [];
  const noCustomer = portal.error instanceof ApiError && portal.error.status === 409;
  const money = (cents: number, cur: string) => `${cur === "EUR" ? "€" : cur + " "}${(cents / 100).toFixed(2)}`;
  const date = (unix: number) => unix ? new Date(unix * 1000).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" }) : "—";

  return (
    <Card className="max-w-3xl">
      <CardHeader>
        <CardTitle className="text-lg">Manage billing</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <p className="text-sm text-muted-foreground">
          Update your payment method, tax details, or cancel via the secure Stripe portal. Your invoices and receipts are below.
        </p>
        <div>
          <Button variant="outline" size="sm" onClick={() => portal.mutate()} disabled={portal.isPending}>
            {portal.isPending ? "Opening…" : "Open billing portal →"}
          </Button>
        </div>
        {noCustomer && <p className="text-sm text-amber-800">Start a subscription first — the billing portal opens once you have an active plan.</p>}
        {portal.isError && !noCustomer && <p className="text-sm text-destructive">{(portal.error as Error).message}</p>}

        <h3 className="mt-6 text-sm font-semibold">Invoices &amp; receipts</h3>
        {invoices.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!invoices.isLoading && rows.length === 0 && <p className="text-sm text-muted-foreground">No invoices yet.</p>}
        {rows.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell>{inv.number ?? inv.id}</TableCell>
                  <TableCell>{date(inv.created)}</TableCell>
                  <TableCell><Badge variant="outline">{inv.status ?? "—"}</Badge></TableCell>
                  <TableCell className="text-right tabular-nums">{money(inv.amountPaidCents || inv.amountDueCents, inv.currency)}</TableCell>
                  <TableCell className="text-right">
                    {inv.invoicePdf && <a href={inv.invoicePdf} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">PDF</a>}
                    {inv.hostedInvoiceUrl && <a href={inv.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" className="ml-3 text-primary hover:underline">View</a>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
